import React from 'react'
import { useIntl } from 'react-intl'
import {
  CircularProgress,
  ClickAwayListener,
  FormControl,
  MenuItem,
  MenuList,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { useKeycloak } from '@react-keycloak/web'
import SubscriptionServices from '../../../../../Services/Consumers/SubscriptionServices'
import { useStyles } from './Styles'
import { PAYABLE_TYPES } from '../../../../../utils/enum'

const PayableAutocomplete = ({
  payableType,
  selectedPayable,
  onPayableSelect,
  handleNotificationCall,
  error,
  touched,
  onBlur
}) => {
  const intl = useIntl()
  const classes = useStyles()
  const { keycloak } = useKeycloak()

  const [searchText, setSearchText] = React.useState('')
  const [isSearching, setIsSearching] = React.useState(false)
  const [searchResults, setSearchResults] = React.useState([])
  const [showDropdown, setShowDropdown] = React.useState(false)
  const debounceTimer = React.useRef(null)

  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  // Search handler
  const handleSearch = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }

    if (!payableType) {
      return
    }

    setIsSearching(true)

    // Determine which API to call based on payable_type
    const getPayables = payableType === PAYABLE_TYPES[0].value
      ? SubscriptionServices.getServices
      : SubscriptionServices.getPlans

    getPayables({
      headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
      params: { title: searchTerm }
    }).then((result) => {
      if (!!result && !!result?.data && result?.data?.status?.code === 200) {
        setSearchResults(result.data.data)
        setShowDropdown(true)
      } else {
        let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
        if (result.response && result.response.data) {
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
        }
        handleNotificationCall({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')
        setSearchResults([])
        setShowDropdown(false)
      }
    }).then(() => {
      setIsSearching(false)
    })
  }

  // Debounced search change handler
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchText(value)
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }
    debounceTimer.current = setTimeout(() => {
      handleSearch(value)
    }, 500)
  }

  // Handle selection
  const handleSelect = (item) => {
    onPayableSelect(item)
    setSearchText('')
    setSearchResults([])
    setShowDropdown(false)
  }

  // Reset search when payable type changes
  React.useEffect(() => {
    setSearchText('')
    setSearchResults([])
    setShowDropdown(false)
  }, [payableType])

  const getPlaceholder = () => {
    if (!payableType) {
      return intl.formatMessage({ id: 'Fields.input.PromocodeRulePayable.SelectTypeFirst.placeholder' })
    }
    return payableType === PAYABLE_TYPES[0].value
      ? intl.formatMessage({ id: 'Fields.input.PromocodeRulePayable.SearchService.placeholder' })
      : intl.formatMessage({ id: 'Fields.input.PromocodeRulePayable.SearchPlan.placeholder' })
  }

  const displayValue = React.useMemo(() => {
    if (selectedPayable) {
      return selectedPayable.title || ''
    }
    return searchText
  }, [selectedPayable, searchText])

  return (
    <FormControl variant="outlined" fullWidth>
      <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
        <div className={classes.autocompleteWrapper}>
          <TextField
            placeholder={getPlaceholder()}
            id="payable_id"
            type={'text'}
            margin="dense"
            variant="outlined"
            fullWidth
            value={displayValue}
            onChange={handleSearchChange}
            onFocus={() => {
              if (searchResults.length > 0 && !selectedPayable) {
                setShowDropdown(true)
              }
            }}
            onBlur={() => {
              if (onBlur) {
                onBlur()
              }
            }}
            disabled={!payableType || !!selectedPayable}
            InputProps={{
              endAdornment: isSearching && <CircularProgress size={20} />
            }}
            error={error && touched}
            helperText={error && touched ? error : ''}
          />

          {/* Dropdown Results */}
          {showDropdown && !selectedPayable && (
            <Paper className={classes.dropdown}>
              {
                searchResults.length > 0 ?
                  <MenuList>
                    {searchResults.map((item) => (
                      <MenuItem
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        className={classes.menuItem}
                      >
                        <div className={classes.userName}>
                          {item.title}
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                  :
                  <Typography variant="body1" className={classes.usersNoResultsText}>
                    {intl.formatMessage({ id: 'Promocodes.Payable.NoResults' })}
                  </Typography>
              }
            </Paper>
          )}
        </div>
      </ClickAwayListener>
    </FormControl>
  )
}

export default PayableAutocomplete

